// Cara Ldn mix & match offer (added by Claude, reversible).
// Display + multi-item add only. Checkout prices come from Shopify automatic discounts.
// Extra items are more of the CURRENT product: each extra item picks its own colour and size.
import { ThemeEvents, CartAddEvent } from '@theme/events';

const TICK =
  '<svg width="12" height="12" viewBox="0 0 13 13" aria-hidden="true" focusable="false"><path d="M2 6.8l2.9 2.9L11 3.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';
const GIFT =
  '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3.5" y="9" width="17" height="11.5" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="2.5" y="6" width="19" height="3.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M12 6v14.5" stroke="currentColor" stroke-width="1.5"/><path d="M12 6C10 2.5 6.5 3 7.5 5.2 8.3 6 12 6 12 6zM12 6c2-3.5 5.5-3 4.5-.8C15.7 6 12 6 12 6z" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const delimit = (n, decimals, thousands, separator) => {
    const [whole, frac] = n.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? separator + frac : '');
  };
  const map = {
    amount: delimit(value, 2, ',', '.'),
    amount_no_decimals: delimit(value, 0, ',', '.'),
    amount_with_comma_separator: delimit(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: delimit(value, 0, '.', ','),
    amount_with_apostrophe_separator: delimit(value, 2, "'", '.'),
    amount_with_space_separator: delimit(value, 2, ' ', ','),
  };
  return (format || '£{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => map[key] ?? map.amount);
}

class CaraMixMatch extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    try {
      this.variants = JSON.parse(this.querySelector('[data-cmm-variants]')?.textContent || '[]');
      this.optionNames = JSON.parse(this.querySelector('[data-cmm-options]')?.textContent || '[]');
    } catch (_) {
      this.variants = [];
      this.optionNames = [];
    }

    this.money = this.dataset.moneyFormat || '£{{amount}}';
    this.d2 = (parseFloat(this.dataset.d2) || 0) / 100;
    this.d3 = (parseFloat(this.dataset.d3) || 0) / 100;
    this.showGift = this.dataset.showGift === 'true';
    this.giftVariant = Number(this.dataset.giftVariant) || 0;
    this.productId = String(this.dataset.productId);
    this.productTitle = this.dataset.productTitle || '';
    this.productImage = this.dataset.productImage || '';

    this.sel = 1;
    this.bag = 0;
    this.cartSubtotal = 0;
    this.cartHasGift = false;
    // Each extra item mirrors item 1 until the shopper changes it.
    this.slots = [
      { values: null, touched: false },
      { values: null, touched: false },
    ];

    this.section = this.closest('.shopify-section') || document;
    this.row = this.querySelector('[data-cmm-row]');
    this.slotsEl = this.querySelector('[data-cmm-slots]');
    this.variant = this.readCurrentVariant();

    this.onClick = this.onClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onVariant = this.onVariant.bind(this);
    this.onCart = this.onCart.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKey);
    this.addEventListener('change', this.onChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariant);
    document.addEventListener(ThemeEvents.cartUpdate, this.onCart);
    // Capture on window runs before the theme's delegated submit handler on document.
    window.addEventListener('submit', this.onSubmit, true);

    this.render();
    this.loadCart();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKey);
    this.removeEventListener('change', this.onChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariant);
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCart);
    window.removeEventListener('submit', this.onSubmit, true);
    this._ready = false;
  }

  get form() {
    return this.section.querySelector('form[data-type="add-to-cart-form"]');
  }

  fmt(cents) {
    return formatMoney(Math.round(cents), this.money);
  }

  fmtWhole(pounds) {
    return formatMoney(pounds * 100, this.money).replace(/([.,])00(?!\d)/, '');
  }

  readCurrentVariant() {
    const id = Number(this.form?.querySelector('input[name="id"]')?.value);
    return (
      this.variants.find((v) => v.id === id) ||
      this.variants.find((v) => v.available) ||
      this.variants[0] || { id: 0, price: 0, compare: 0, options: [] }
    );
  }

  /* ---------- variant helpers ---------- */

  optionIndex(pattern) {
    return this.optionNames.findIndex((n) => pattern.test(String(n)));
  }

  valuesFor(optionIndex) {
    return [...new Set(this.variants.map((v) => v.options[optionIndex]))];
  }

  // Options with more than one value, colour first, then size, then anything else.
  get selectOrder() {
    if (this._order) return this._order;
    const rank = (i) => (/colo/i.test(this.optionNames[i]) ? 0 : /size/i.test(this.optionNames[i]) ? 1 : 2);
    this._order = this.optionNames
      .map((_, i) => i)
      .filter((i) => this.valuesFor(i).length > 1)
      .sort((a, b) => rank(a) - rank(b) || a - b);
    return this._order;
  }

  findVariant(values) {
    return this.variants.find((v) => v.options.every((o, k) => o === values[k]));
  }

  isAvailable(values, optionIndex, value) {
    const test = [...values];
    test[optionIndex] = value;
    const v = this.findVariant(test);
    return Boolean(v && v.available);
  }

  slotValues(i) {
    const slot = this.slots[i];
    if (!slot.touched || !slot.values) slot.values = [...(this.variant.options || [])];
    return slot.values;
  }

  slotVariant(i) {
    return this.findVariant(this.slotValues(i));
  }

  imageFor(variant) {
    if (variant?.image) return variant.image;
    const colour = this.optionIndex(/colo/i);
    if (variant && colour >= 0) {
      const match = this.variants.find((v) => v.image && v.options[colour] === variant.options[colour]);
      if (match) return match.image;
    }
    return this.productImage;
  }

  // Keep the option the shopper just changed; move size to the nearest one in stock, else any in-stock variant.
  repair(values, changedIndex) {
    const current = this.findVariant(values);
    if (current?.available) return values;
    const size = this.optionIndex(/size/i);
    if (size >= 0 && size !== changedIndex) {
      const sizes = this.valuesFor(size);
      const start = sizes.indexOf(values[size]);
      for (let d = 1; d < sizes.length; d++) {
        for (const j of [start - d, start + d]) {
          if (j < 0 || j >= sizes.length) continue;
          const test = [...values];
          test[size] = sizes[j];
          const v = this.findVariant(test);
          if (v?.available) return test;
        }
      }
    }
    const fallback = this.variants.find((v) => v.available && v.options[changedIndex] === values[changedIndex]);
    return fallback ? [...fallback.options] : values;
  }

  /* ---------- cart ---------- */

  async loadCart() {
    try {
      const cartUrl = (window.Theme?.routes?.cart_url || '/cart') + '.js';
      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      let count = 0;
      let subtotal = 0;
      this.cartHasGift = false;
      (cart.items || []).forEach((item) => {
        if (this.giftVariant && item.variant_id === this.giftVariant) {
          this.cartHasGift = true;
          return;
        }
        count += item.quantity;
        subtotal += item.original_line_price ?? item.price * item.quantity;
      });
      this.bag = count;
      this.cartSubtotal = subtotal;
    } catch (_) {
      this.bag = 0;
      this.cartSubtotal = 0;
    }
    this.render();
  }

  /* ---------- maths ---------- */

  calc(q) {
    const bagUI = Math.min(this.bag, 2);
    const extra = Math.max(0, q - bagUI - 1);
    const count = this.bag + 1 + extra;
    const d = count >= 3 ? this.d3 : count === 2 ? this.d2 : 0;
    let newSub = this.variant.price;
    let newRef = Math.max(this.variant.compare || 0, this.variant.price);
    for (let i = 0; i < extra; i++) {
      const v = this.slotVariant(i) || this.variant;
      newSub += v.price;
      newRef += Math.max(v.compare || 0, v.price);
    }
    const total = (this.cartSubtotal + newSub) * (1 - d);
    return {
      q,
      extra,
      count,
      d,
      newCount: 1 + extra,
      newTot: newSub * (1 - d),
      newRef,
      save: this.cartSubtotal + newRef - total,
    };
  }

  /* ---------- render ---------- */

  render() {
    if (!this.row) return;
    const bagUI = Math.min(this.bag, 2);
    if (this.sel <= bagUI) this.sel = Math.min(3, bagUI + 1);

    const note = this.querySelector('[data-cmm-note]');
    if (note) {
      note.hidden = this.bag === 0;
      note.textContent = this.bag ? `${this.bag} item${this.bag > 1 ? 's' : ''} already in your cart count towards this.` : '';
    }

    const v = this.variant;
    const was = Math.max(v.compare || 0, v.price);
    const pct = (d) => Math.round(d * 100);
    const giftLabel = this.dataset.giftLabel || 'Free gift';

    const perkShipping = this.dataset.perkShipping || '';
    let cards = '';
    [1, 2, 3].forEach((q) => {
      const d = q === 3 ? this.d3 : q === 2 ? this.d2 : 0;
      const disabled = q <= bagUI;
      const on = this.sel === q;
      const each = Math.round(v.price * (1 - d));
      const total = Math.round(v.price * q * (1 - d));
      const save = Math.floor((was * q - total) / 100 + 1e-6);
      const perks = [];
      if (perkShipping) perks.push(perkShipping);
      if (q >= 2 && this.showGift) perks.push(giftLabel);
      if (d > 0) perks.push(`${pct(d)}% off`);
      cards +=
        `<button type="button" class="cmm-card" role="radio" data-q="${q}" aria-checked="${on}"` +
        `${disabled ? ' aria-disabled="true"' : ''} tabindex="${on ? 0 : -1}">` +
        (q === 3 && this.dataset.bestLabel ? `<span class="cmm-tag">${esc(this.dataset.bestLabel)}</span>` : '') +
        `<span class="cmm-buy">Buy ${q}</span>` +
        `<span class="cmm-each">${this.fmt(each)}</span><span class="cmm-per">per item</span>` +
        '<span class="cmm-rule"></span>' +
        (disabled
          ? `<span class="cmm-incart">${TICK} In your cart</span>`
          : (save >= 1
              ? `<span class="cmm-save">Save ${this.fmtWhole(save)}</span>`
              : '<span class="cmm-save cmm-save--none">Full price</span>') +
            `<span class="cmm-total">${this.fmt(total)} total</span>` +
            (perks.length
              ? `<span class="cmm-perks">${perks.map((p) => `<span class="cmm-perk">${TICK}${esc(p)}</span>`).join('')}</span>`
              : '')) +
        '</button>';
    });
    this.row.innerHTML = cards;

    const k = this.calc(this.sel);
    let rows = '';
    for (let i = 0; i < k.extra; i++) {
      const values = this.slotValues(i);
      const variant = this.slotVariant(i);
      const image = this.imageFor(variant);
      let selects = '';
      this.selectOrder.forEach((opt) => {
        const name = this.optionNames[opt];
        const isSize = /size/i.test(String(name));
        selects +=
          `<select class="cmm-isel" data-slot="${i}" data-opt="${opt}" aria-label="${esc(name)} for item ${i + 2}">` +
          this.valuesFor(opt)
            .map((val) => {
              const ok = this.isAvailable(values, opt, val);
              const label = ok ? (isSize ? `Size ${val}` : val) : `${val} – sold out`;
              return `<option value="${esc(val)}"${val === values[opt] ? ' selected' : ''}${ok ? '' : ' disabled'}>${esc(label)}</option>`;
            })
            .join('') +
          '</select>';
      });
      const price = variant ? this.fmt(Math.round(variant.price * (1 - k.d))) : '';
      rows +=
        '<div class="cmm-irow">' +
        `<span class="cmm-ithumb">${image ? `<img src="${esc(image)}" alt="" width="48" height="48" loading="lazy">` : ''}</span>` +
        `<span class="cmm-imeta"><span class="cmm-ititle">${esc(this.productTitle)}</span>` +
        `<span class="cmm-iprice">${price} · Item ${i + 2}</span></span>` +
        (selects ? `<span class="cmm-isels">${selects}</span>` : '<span class="cmm-ione">Another one</span>') +
        '</div>';
    }
    if (this.showGift && k.count >= 2) {
      const giftImage = this.dataset.giftImage
        ? `<img src="${esc(this.dataset.giftImage)}" alt="" width="40" height="40" loading="lazy">`
        : GIFT;
      rows +=
        `<div class="cmm-slot cmm-slot--gift"><span class="cmm-thumb">${giftImage}</span>` +
        `<span class="cmm-sm"><span class="cmm-sn">${esc(this.dataset.giftTitle || giftLabel)}</span>` +
        `<span class="cmm-ss">${this.giftVariant ? 'Added to your order automatically' : 'Included with 2 or more items'}</span></span>` +
        '<span class="cmm-free">FREE</span></div>';
    }
    this.slotsEl.innerHTML = rows;

    const applies = this.querySelector('[data-cmm-applies]');
    if (applies) {
      const show = this.bag > 0 && k.d > 0;
      applies.hidden = !show;
      applies.textContent = show
        ? `${pct(k.d)}% off also applies to the ${this.bag} item${this.bag > 1 ? 's' : ''} already in your cart`
        : '';
    }

    this.paintAtc();
  }

  paintAtc() {
    const button = this.form?.querySelector('[ref="addToCartButton"], button[name="add"]');
    const label = button?.querySelector('.add-to-cart-text__content');
    if (!button || !label) return;

    const ours = label.querySelector('.cmm-atc');
    if (!ours) label.dataset.cmmOrig = label.textContent.trim();
    const original = label.dataset.cmmOrig || label.textContent.trim();

    const k = this.calc(this.sel);
    if (button.disabled || (k.newCount <= 1 && k.d === 0)) {
      if (ours) label.textContent = original;
      return;
    }
    const strike = k.newRef > k.newTot + 0.5 ? `<s>${this.fmt(k.newRef)}</s>` : '';
    const text = k.newCount > 1 ? `Add ${k.newCount} items` : original;
    label.innerHTML = `<span class="cmm-atc">${strike}${esc(text)} · ${this.fmt(k.newTot)}</span>`;
  }

  /* ---------- events ---------- */

  onClick(event) {
    const target = event.target.closest('[data-q]');
    if (!target || target.getAttribute('aria-disabled') === 'true') return;
    this.sel = Number(target.dataset.q);
    this.render();
    if (target.classList.contains('cmm-card')) this.row.querySelector(`.cmm-card[data-q="${this.sel}"]`)?.focus();
  }

  onKey(event) {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    if (!event.target.closest('.cmm-card')) return;
    event.preventDefault();
    const dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const bagUI = Math.min(this.bag, 2);
    let q = this.sel;
    do {
      q += dir;
    } while (q >= 1 && q <= 3 && q <= bagUI);
    if (q < 1 || q > 3) return;
    this.sel = q;
    this.render();
    this.row.querySelector(`.cmm-card[data-q="${q}"]`)?.focus();
  }

  onChange(event) {
    const select = event.target.closest('select[data-slot]');
    if (!select) return;
    const i = Number(select.dataset.slot);
    const opt = Number(select.dataset.opt);
    const values = [...this.slotValues(i)];
    values[opt] = select.value;
    this.slots[i] = { values: this.repair(values, opt), touched: true };
    this.render();
    this.querySelector(`select[data-slot="${i}"][data-opt="${opt}"]`)?.focus();
  }

  onVariant(event) {
    const detail = event.detail || {};
    const productId = detail.data?.productId;
    if (productId && String(productId) !== this.productId) return;
    if (detail.data?.newProduct) return;
    const r = detail.resource;
    if (!r) return;
    this.variant = this.variants.find((v) => v.id === Number(r.id)) || {
      id: Number(r.id),
      price: r.price,
      compare: r.compare_at_price || 0,
      available: r.available,
      options: r.options || [],
      image: r.featured_image?.src || null,
    };
    this.render();
    // The theme morphs the Add to cart button after this event; repaint once it has.
    [80, 300].forEach((t) => setTimeout(() => this.paintAtc(), t));
  }

  onCart() {
    this.loadCart();
  }

  async onSubmit(event) {
    const form = this.form;
    if (!form || event.target !== form) return;

    const k = this.calc(this.sel);
    const needGift = Boolean(this.giftVariant) && k.count >= 2 && !this.cartHasGift;
    if (k.extra === 0 && !needGift) return; // single item: let the theme handle it

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this._busy) return;
    this._busy = true;

    const errorEl = this.querySelector('[data-cmm-error]');
    if (errorEl) errorEl.hidden = true;

    try {
      const mainId = Number(form.querySelector('input[name="id"]')?.value) || this.variant.id;
      const quantities = new Map();
      const add = (id) => quantities.set(id, (quantities.get(id) || 0) + 1);
      add(mainId);
      for (let i = 0; i < k.extra; i++) {
        const v = this.slotVariant(i);
        if (!v || !v.available) throw new Error(`Item ${i + 2} is sold out in that colour and size. Please choose another.`);
        add(v.id);
      }
      const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));
      if (needGift) items.push({ id: this.giftVariant, quantity: 1 });

      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const body = { items };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'These items could not be added. Please try again.');
      }

      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const source = form.closest('product-form-component') || this;
      source.dispatchEvent(
        new CartAddEvent({}, String(mainId), {
          source: 'product-form-component',
          itemCount,
          productId: this.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    } finally {
      this._busy = false;
    }
  }
}

if (!customElements.get('cara-mix-match')) {
  customElements.define('cara-mix-match', CaraMixMatch);
}
